import { useState, useEffect } from 'react';
import { Calendar, CheckCircle, Package, Truck } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const VehicleHistory = ({ user }) => {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthInfo, setMonthInfo] = useState({ month: '', total: 0 });
  const [error, setError] = useState(null);
  const [hoursVisibilityEnabled, setHoursVisibilityEnabled] = useState(false);
  const [hoursTotals, setHoursTotals] = useState({ weekly: 0, monthly: 0, week_key: '' });

  useEffect(() => {
    fetchValidatedMissions();
  }, []);

  const fetchValidatedMissions = async () => {
    try {
      const response = await axios.get(`${API}/employee/validated-missions`, { withCredentials: true });
      setMissions(response.data.missions || []);
      setHoursVisibilityEnabled(response.data.hours_visibility_enabled === true);
      setHoursTotals(response.data.hours_totals || { weekly: 0, monthly: 0, week_key: '' });
      setMonthInfo({
        month: response.data.month,
        total: response.data.total_missions,
        start_date: response.data.start_date,
        end_date: response.data.end_date
      });
    } catch (error) {
      console.error('Failed to fetch missions:', error);
      setError('Impossible de charger l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const formatWorkedHours = (hours) => {
    if (hours === null || hours === undefined || Number.isNaN(Number(hours))) {
      return '-';
    }
    return `${Number(hours).toFixed(2).replace('.', ',')} h`;
  };

  const getPlatformBadge = (platform) => {
    if (platform === 'CP') {
      return <Badge className="bg-purple-500 text-white">Colis Privé</Badge>;
    }
    return <Badge className="bg-blue-500 text-white">Amazon</Badge>;
  };

  const getTourTypeDisplay = (mission) => {
    if (mission.platform === 'CP') {
      const stats = [];
      if (mission.delivered !== null && mission.delivered !== undefined) {
        stats.push(`${mission.delivered} livrés`);
      }
      if (mission.returned !== null && mission.returned !== undefined) {
        stats.push(`${mission.returned} retours`);
      }
      if (mission.picked !== null && mission.picked !== undefined) {
        stats.push(`${mission.picked} ramassés`);
      }
      return stats.length > 0 ? stats.join(' | ') : 'Livraison CP';
    }
    return mission.tour_type || 'Tournée AMZ';
  };

  const renderMonthSummary = () => (
    <Card className="bg-gradient-to-r from-green-600 to-green-800 text-white" data-testid="history-month-summary-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Calendar className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="text-white/80 text-sm">Mois en cours</p>
              <p className="text-2xl font-bold capitalize">{monthInfo.month}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-white/80 text-sm">Missions validées</p>
            <p className="text-4xl font-bold">{monthInfo.total}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderHoursTotals = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="hours-totals-summary-grid">
      <Card data-testid="weekly-hours-total-card">
        <CardContent className="p-4">
          <p className="text-sm text-gray-500">Total hebdomadaire (lecture seule)</p>
          <p className="text-2xl font-bold text-[#0F172A]" data-testid="weekly-hours-total-value">
            {formatWorkedHours(hoursTotals.weekly)}
          </p>
        </CardContent>
      </Card>
      <Card data-testid="monthly-hours-total-card">
        <CardContent className="p-4">
          <p className="text-sm text-gray-500">Total mensuel (lecture seule)</p>
          <p className="text-2xl font-bold text-[#0F172A]" data-testid="monthly-hours-total-value">
            {formatWorkedHours(hoursTotals.monthly)}
          </p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <Layout user={user}>
      <div className="space-y-6 fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] font-[Chivo]">Historique des Missions</h1>
          <p className="text-gray-500">Missions validées du mois en cours</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
          </div>
        ) : error ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-red-500">{error}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {renderMonthSummary()}
            {hoursVisibilityEnabled && renderHoursTotals()}

            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="text-blue-700 font-medium">Information</p>
                    <p className="text-blue-600 text-sm">
                      Seules les missions validées par l&apos;administration apparaissent ici.
                      L&apos;historique est réinitialisé chaque 1er du mois.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {missions.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 text-lg font-medium">Aucune mission validée ce mois-ci</p>
                  <p className="text-gray-400 text-sm mt-2">
                    Vos missions apparaîtront ici une fois validées par l&apos;administration
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Missions Validées ({missions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="employee-hours-missions-table">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-4 font-medium text-gray-600">Date</th>
                          {hoursVisibilityEnabled && (
                            <th className="text-left p-4 font-medium text-gray-600">Mission</th>
                          )}
                          <th className="text-left p-4 font-medium text-gray-600">Plateforme</th>
                          <th className="text-left p-4 font-medium text-gray-600">Prestation</th>
                          <th className="text-left p-4 font-medium text-gray-600">Véhicule</th>
                          {hoursVisibilityEnabled && (
                            <>
                              <th className="text-left p-4 font-medium text-gray-600">Début</th>
                              <th className="text-left p-4 font-medium text-gray-600">Fin</th>
                              <th className="text-left p-4 font-medium text-gray-600">Heures effectuées</th>
                            </>
                          )}
                          <th className="text-center p-4 font-medium text-gray-600">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {missions.map((mission, index) => (
                          <tr key={`${mission.mission_id || mission.date}-${index}`} className="border-b hover:bg-gray-50">
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span className="font-medium">{formatDate(mission.date)}</span>
                              </div>
                            </td>
                            {hoursVisibilityEnabled && (
                              <td className="p-4" data-testid={`mission-id-row-${index}`}>
                                <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">{mission.mission_id || `mission_${mission.date}`}</span>
                              </td>
                            )}
                            <td className="p-4">{getPlatformBadge(mission.platform)}</td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                {mission.platform === 'CP' ? (
                                  <Package className="w-4 h-4 text-purple-500" />
                                ) : (
                                  <Truck className="w-4 h-4 text-blue-500" />
                                )}
                                <span className="text-gray-700">{getTourTypeDisplay(mission)}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              {mission.vehicle_plate ? (
                                <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">
                                  {mission.vehicle_plate}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            {hoursVisibilityEnabled && (
                              <>
                                <td className="p-4" data-testid={`mission-start-time-row-${index}`}>{mission.start_time || '-'}</td>
                                <td className="p-4" data-testid={`mission-end-time-row-${index}`}>{mission.end_time || '-'}</td>
                                <td className="p-4 font-semibold text-slate-700" data-testid={`mission-worked-hours-row-${index}`}>
                                  {formatWorkedHours(mission.worked_hours)}
                                </td>
                              </>
                            )}
                            <td className="p-4 text-center">
                              <Badge className="bg-green-100 text-green-700 border border-green-200">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Validé
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {missions.length > 0 && missions.some(m => m.platform === 'CP') && (
          <Card>
            <CardHeader>
              <CardTitle className="text-purple-700 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Résumé Colis Privé - {monthInfo.month}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">
                    {missions.filter(m => m.platform === 'CP').reduce((acc, m) => acc + (m.delivered || 0), 0)}
                  </p>
                  <p className="text-sm text-green-700">Colis livrés</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <p className="text-3xl font-bold text-orange-600">
                    {missions.filter(m => m.platform === 'CP').reduce((acc, m) => acc + (m.returned || 0), 0)}
                  </p>
                  <p className="text-sm text-orange-700">Retours</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">
                    {missions.filter(m => m.platform === 'CP').reduce((acc, m) => acc + (m.picked || 0), 0)}
                  </p>
                  <p className="text-sm text-blue-700">Ramassés</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {missions.length > 0 && missions.some(m => m.platform === 'AMZ') && (
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-700 flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Résumé Amazon - {monthInfo.month}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                {['NEXT', 'SDA', 'SDB', 'SDC', 'SDA+C', 'SDA+B', 'SDB+C'].map(tourType => {
                  const count = missions.filter(m => 
                    m.platform === 'AMZ' && 
                    m.tour_type && 
                    m.tour_type.toUpperCase() === tourType.toUpperCase()
                  ).length;
                  return (
                    <div key={tourType} className={`text-center p-3 rounded-lg ${count > 0 ? 'bg-blue-50' : 'bg-gray-50'}`}>
                      <p className={`text-2xl font-bold ${count > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                        {count}
                      </p>
                      <p className={`text-xs ${count > 0 ? 'text-blue-700' : 'text-gray-500'}`}>{tourType}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default VehicleHistory;
